/// <amd-module name="Test/TestPlansHubRefresh/TestCaseResultsPanel/Redux/TestCaseResultsPanelSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import {
    TestCaseResultsPanelActions,
    TestCaseResultsPanelActionsTypes
} from "Test/TestPlansHubRefresh/TestCaseResultsPanel/Redux/TestCaseResultsPanelActions";
import * as TestCaseResultsPanelConstants from "Test/TestPlansHubRefresh/TestCaseResultsPanel/TestCaseResultsPanelConstants";
import { TelemetryConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux//TelemetryActions";
import { isAdvancedLicenseEnabled } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import { errorHandledSaga } from "Test/TestPlansHubRefresh/TestHub/Util";
import { getDataProviderContentForTestResultsAsync } from "Test/TestPlansHubRefresh/TestCaseResultsPanel/Utils"
import { ITfsPageService } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLocationService } from "VSS/Platform/Location";

export function* TestCaseResultsPanelSaga(): SagaIterator {
    yield effects.takeLatest(TestCaseResultsPanelActionsTypes.fetchTestCaseResults, _fetchTestCaseResults);
    yield effects.takeLatest(TestCaseResultsPanelActionsTypes.openTestExecutionHistory, _openTestExecutionHistory);
    yield effects.takeLatest(TestCaseResultsPanelActionsTypes.openRunsHub, _openRunsHub);
}

export function* fetchTestCaseResults(
    action: ActionsOfType<TestCaseResultsPanelActions, TestCaseResultsPanelActionsTypes.fetchTestCaseResults>
): SagaIterator {
    yield effects.put(TestCaseResultsPanelActions.loadingInProgress());
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    if (!pageData || !pageData.project) {
        throw new Error("Project missing");
    }

    let providerContext = {
        testCaseId: action.payload.testCaseId
    }

    let data = yield effects.call(getDataProviderContentForTestResultsAsync, pageContext, providerContext) as any;

    if (data) {
        yield effects.put(TestCaseResultsPanelActions.fetchTestResultsSucceeded(data));
    }
}

export function* openTestExecutionHistory(action: ActionsOfType<TestCaseResultsPanelActions, TestCaseResultsPanelActionsTypes.openTestExecutionHistory>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    let projectName = "";
    if (tfsPageData && tfsPageData.project) {
        projectName = tfsPageData.project.name;
    }

    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
    const routeValues: { [key: string]: string } = {
        project: projectName
    };

    let executionHistoryUrl = (yield effects.call(
        [locationService, locationService.routeUrl],
        TestCaseResultsPanelConstants.TestExecutionHistoryRouteId,
        routeValues
    )) as any;
    executionHistoryUrl += format(TestCaseResultsPanelConstants.queryParams_ExecutionHistory, action.payload.testCaseId, action.payload.pointId);

    const newWindow = (yield effects.call([window, window.open], executionHistoryUrl, "_blank")) as any;
    if (newWindow) {
        newWindow.opener = null;
    }

    const telemetryPayload = {
        pointId: action.payload.pointId,
        testCaseId: action.payload.testCaseId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.OPEN_TEST_EXECUTION_HISTORY, telemetryPayload));
}

export function* openRunsHub(action: ActionsOfType<TestCaseResultsPanelActions, TestCaseResultsPanelActionsTypes.openRunsHub>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    let projectName = "";
    if (tfsPageData && tfsPageData.project) {
        projectName = tfsPageData.project.name;
    }

    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
    const routeValues: { [key: string]: string } = {
        project: projectName
    };
    // constructing new plan result using contribution id
    let resultUrl = (yield effects.call([locationService, locationService.routeUrl], TestCaseResultsPanelConstants.TestRunsPageRouteId, routeValues)) as any;
    resultUrl += format(TestCaseResultsPanelConstants.TestRunsPageQueryParams, action.payload.runId, action.payload.resultId);

    const newWindow = (yield effects.call([window, window.open], resultUrl, "_blank")) as any;
    if (newWindow) {
        newWindow.opener = null;
    }

    const telemetryPayload = {
        pointId: action.payload.pointId,
        runId: action.payload.runId,
        resultId: action.payload.resultId
    };
    yield effects.put(TelemetryActions.publishTelemetry(TestCaseResultsPanelConstants.OPEN_RUNS_HUB, telemetryPayload));
}

const _fetchTestCaseResults = errorHandledSaga(function* (
    action: ActionsOfType<TestCaseResultsPanelActions, TestCaseResultsPanelActionsTypes.fetchTestCaseResults>
): SagaIterator {
    yield effects.call(fetchTestCaseResults, action);
});

const _openTestExecutionHistory = errorHandledSaga(function* (
    action: ActionsOfType<TestCaseResultsPanelActions, TestCaseResultsPanelActionsTypes.openTestExecutionHistory>
): SagaIterator {
    yield effects.call(openTestExecutionHistory, action);
});

const _openRunsHub = errorHandledSaga(function* (
    action: ActionsOfType<TestCaseResultsPanelActions, TestCaseResultsPanelActionsTypes.openRunsHub>
): SagaIterator {
    yield effects.call(openRunsHub, action);
});